import { Injectable } from '@angular/core';
import { ProtectionService } from '@services/core-services/protection.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TenantWithAuthApiService } from '@services/tenant-services/tenant-api.service';
import { OrganizationUser } from '@services/user-services/models';
import { User } from '@services/core-services/models';
import { UrlService } from '@services/auth-services/url.service';

@Injectable({ providedIn: 'root' })
export class OrganizationUsersService {
  private NO_PERMISSIONS_KEY = '0';
  private usersSubject = new BehaviorSubject<OrganizationUser[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  users$ = this.usersSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  constructor(
    private readonly api: TenantWithAuthApiService,
    private readonly protectionService: ProtectionService,
    private readonly urlService: UrlService
  ) {}

  loadUsers(): Observable<OrganizationUser[]> {
    this.loadingSubject.next(true);
    const tenant = this.protectionService.getTenant()!;
    return this.api.get<any>(`tenants/${tenant.id}/users`).pipe(
      map(response => response.content.users as OrganizationUser[]),
      tap(users => {
        this.usersSubject.next(users);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        console.error('Failed to load users:', error);
        this.usersSubject.next([]);
        return throwError(() => error);
      })
    );
  }

  getUsers(): Observable<OrganizationUser[]> {
    return this.users$;
  }

  inviteUser(payload: Partial<OrganizationUser>): Observable<OrganizationUser> {
    this.loadingSubject.next(true);
    const tenant = this.protectionService.getTenant()!;
    const { roles, ...rest } = payload;
    const { subdomain, domain, protocol } = this.urlService.getUrlSettings();
    const continue_url = `${protocol}//${domain}?subdomain=${subdomain}`;
    const params = {
      ...rest,
      roles: (roles || []).filter(r => r.role_id !== this.NO_PERMISSIONS_KEY && r.role_id),
      continue_url,
    };

    return this.api
      .post<
        Partial<OrganizationUser>,
        any
      >(`tenants/${tenant.id}/users`, this.sanitizeRequest(params))
      .pipe(
        map(response => response?.content?.user as OrganizationUser),
        tap(newUser => {
          if (newUser) {
            const currentUsers = this.usersSubject.getValue();
            this.usersSubject.next([...currentUsers, newUser]);
          }
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Failed to invite user:', error);
          return throwError(() => error);
        })
      );
  }

  updateUser(id: string, userUpdate: Partial<OrganizationUser>): Observable<OrganizationUser> {
    this.loadingSubject.next(true);
    const tenant = this.protectionService.getTenant()!;

    return this.api
      .put<
        Partial<OrganizationUser>,
        any
      >(`tenants/${tenant.id}/users/${id}`, this.sanitizeRequest(userUpdate))
      .pipe(
        map(response => response.content.user as OrganizationUser),
        tap(updatedUser => {
          if (updatedUser) {
            const currentUsers = this.usersSubject.getValue();
            const index = currentUsers.findIndex(u => u.id === id);
            if (index !== -1) {
              const newUsers = [...currentUsers];
              newUsers[index] = updatedUser;
              this.usersSubject.next(newUsers);
            }
          }
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Failed to update user:', error);
          return throwError(() => error);
        })
      );
  }

  deleteUser(id: string): Observable<void> {
    this.loadingSubject.next(true);
    const tenant = this.protectionService.getTenant()!;
    return this.api.delete<any>(`tenants/${tenant.id}/users/${id}`).pipe(
      map(() => void 0),
      tap(() => {
        const currentUsers = this.usersSubject.getValue();
        this.usersSubject.next(currentUsers.filter(u => u.id !== id));
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        console.error('Failed to delete user:', error);
        return throwError(() => error);
      })
    );
  }

  getCurrentUser(): Observable<User> {
    const tenant = this.protectionService.getTenant()!;
    const currentUser = this.protectionService.getUser();

    return this.api
      .get<any>(`tenants/${tenant.id}/users/${currentUser!.uid}`)
      .pipe(map(response => response.content.user as User));
  }

  getSingleUser(id: string): Observable<User> {
    return this.api
      .get<any>(`tenants/${id}/users`)
      .pipe(map(response => response.content.user as User));
  }

  /**
   * Uploads tenant image and returns image_url
   * @param file
   */
  uplodProfileImage(file: File, id: string): Observable<{ image_url: string }> {
    const tenant = this.protectionService.getTenant()!;
    const data = new FormData();
    data.set('file', file);
    return this.api
      .post<any, any>(`tenants/${tenant.id}/users/${id}/upload-image`, data)
      .pipe(map(response => response.content));
  }

  deleteProfileImage(id: string) {
    const tenant = this.protectionService.getTenant()!;
    return this.api.delete<any>(`tenants/${tenant.id}/users/${id}/upload-image`);
  }

  setDefaultStore(storeId: string) {
    const tenant = this.protectionService.getTenant()!;
    const currentUser = this.protectionService.getUser();
    return this.api.put<any, any>(`tenants/${tenant.id}/users/${currentUser?.uid}/default-store`, {
      store_id: storeId,
    });
  }

  private sanitizeRequest(payload: Partial<OrganizationUser>): Partial<OrganizationUser> {
    return {
      ...payload,
      phone: payload.phone || undefined,
    };
  }
}
